import {
  unlockNotificationAudio,
  playAlertSoundForType,
} from "../../notifications/soundUtils";
import { NOTIFICATION_TYPES } from "../../notifications/types";
import { isPageInactive } from "../../notifications/desktopNotification";

const PERMISSION_ASKED_KEY = "xpro_webchat_notif_asked_v1";

function previewFromMessage(msg, fallback = "Mensagem") {
  if (!msg || typeof msg !== "object") return fallback;
  const type = String(msg.type || "").toLowerCase();
  const raw = String(msg.text || msg.content || msg.mensagem || "").trim();
  if (raw && raw !== "[mensagem]") {
    if (type === "image" && /^imagem$/i.test(raw)) return "📷";
    if (type === "video" && /^vídeo$/i.test(raw)) return "🎬";
    if (type === "audio") return "🎵";
    if (type === "location" && /^localiza/i.test(raw)) return "📍";
    if (type === "sticker") return "Figurinha";
    return raw.length > 120 ? `${raw.slice(0, 117)}…` : raw;
  }
  if (type === "image") return "📷";
  if (type === "video") return "🎬";
  if (type === "audio") return "🎵";
  if (type === "file" || type === "document") {
    return msg.document?.name || "📄";
  }
  if (type === "location") return "📍";
  if (type === "contact") return msg.contact?.name || "👤";
  if (type === "sticker") return "Figurinha";
  return fallback;
}

/** Desbloqueia áudio no primeiro gesto do visitante (política autoplay). */
export function unlockWebchatVisitorAudio() {
  unlockNotificationAudio();
}

/**
 * Pede permissão de Notification após gesto do usuário (uma vez por origem).
 * @returns {Promise<"granted"|"denied"|"default"|"unsupported">}
 */
export async function ensureWebchatNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    if (sessionStorage.getItem(PERMISSION_ASKED_KEY) === "1") {
      return Notification.permission;
    }
    sessionStorage.setItem(PERMISSION_ASKED_KEY, "1");
  } catch {
    /* ignore */
  }
  try {
    const result = await Notification.requestPermission();
    return result || Notification.permission;
  } catch {
    return Notification.permission;
  }
}

/**
 * Alerta sonoro sempre; notificação nativa só com aba/janela inativa.
 * @param {{ title: string, body?: string, tag?: string, playSound?: boolean, showDesktop?: boolean }} opts
 */
export async function alertWebchatVisitorIncoming(opts = {}) {
  const {
    title = "Atendimento",
    body = "",
    tag = "webchat-incoming",
    playSound = true,
    showDesktop = true,
  } = opts;

  if (playSound) {
    void playAlertSoundForType(NOTIFICATION_TYPES.chat_message);
  }

  if (!showDesktop) return;
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (!isPageInactive()) return;

  const options = {
    body: body || undefined,
    tag,
    renotify: true,
    silent: true, // som já tocamos via Web Audio / WAV
  };

  try {
    const n = new Notification(title, options);
    n.onclick = () => {
      try {
        window.focus();
        if (window.parent && window.parent !== window) {
          window.parent.focus();
        }
      } catch {
        /* ignore */
      }
      n.close();
    };
  } catch {
    /* ignore */
  }
}

/**
 * @param {object[]} agentMessages
 * @param {{ brandName?: string, fallbackBody?: string, multiBody?: string }} [labels]
 */
export function notifyWebchatAgentMessages(agentMessages, labels = {}) {
  const list = Array.isArray(agentMessages) ? agentMessages.filter(Boolean) : [];
  if (!list.length) return;

  const last = list[list.length - 1];
  const title = String(labels.brandName || "Atendimento").trim() || "Atendimento";
  const body =
    list.length > 1
      ? labels.multiBody || `${list.length} ${labels.fallbackBody || "mensagens novas"}`
      : previewFromMessage(last, labels.fallbackBody || "Mensagem");

  void alertWebchatVisitorIncoming({
    title,
    body,
    tag: `webchat-msg-${last.id || Date.now()}`,
  });
}

export { previewFromMessage };
